import { useState } from 'react';
import { supabase } from '../../../supabaseClient';

// ─── Icons ─────────────────────────────────────────────────────────────────────

const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// ─── Password field with show/hide toggle ──────────────────────────────────────

function PasswordField({ label, description, value, onChange, error }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      <label className="font-poppins font-semibold text-white text-sm">{label}</label>
      {description && (
        <p className="font-poppins text-white/40 text-xs -mt-0.5">{description}</p>
      )}
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="font-poppins text-sm text-white w-full rounded-xl px-4 py-3 pr-12 focus:outline-none transition-colors duration-200"
          style={{
            backgroundColor: '#2A2A2A',
            border: `1px solid ${error ? 'rgba(248,113,113,0.5)' : 'rgba(255,255,255,0.1)'}`,
          }}
          onFocus={e => { if (!error) e.target.style.borderColor = 'rgba(247,148,29,0.5)'; }}
          onBlur={e => { if (!error) e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
        />
        <button
          type="button"
          onClick={() => setVisible(v => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 focus:outline-none transition-colors duration-200"
          style={{ color: visible ? '#F7941D' : 'rgba(255,255,255,0.3)' }}
          onMouseEnter={e => { if (!visible) e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
          onMouseLeave={e => { if (!visible) e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; }}
          tabIndex={-1}
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      {error && (
        <p className="font-poppins text-xs font-medium" style={{ color: '#f87171' }}>{error}</p>
      )}
    </div>
  );
}

// ─── Main section ──────────────────────────────────────────────────────────────

function ChangePasswordSection() {
  const [current, setCurrent]     = useState('');
  const [newPass, setNewPass]     = useState('');
  const [confirm, setConfirm]     = useState('');
  const [saving, setSaving]       = useState(false);
  const [currentError, setCurrentError] = useState('');
  const [status, setStatus]       = useState(null); // 'success' | null

  const mismatch   = confirm.length > 0 && newPass !== confirm;
  const canSave    = current.length > 0 && newPass.length > 0 && confirm.length > 0 && !mismatch && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setCurrentError('');
    setStatus(null);

    // Step 1 — verify current password by re-signing in
    const { data: userData } = await supabase.auth.getUser();
    const email = userData?.user?.email;

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password: current,
    });

    if (signInError) {
      setCurrentError('Current password is incorrect.');
      setSaving(false);
      return;
    }

    // Step 2 — update to new password
    const { error: updateError } = await supabase.auth.updateUser({ password: newPass });

    setSaving(false);

    if (updateError) {
      setCurrentError(updateError.message || 'Something went wrong. Please try again.');
    } else {
      setStatus('success');
      setCurrent('');
      setNewPass('');
      setConfirm('');
      setTimeout(() => setStatus(null), 5000);
    }
  };

  return (
    <div className="flex flex-col gap-8 max-w-2xl">

      {/* Header */}
      <div>
        <h1 className="font-poppins font-bold text-white text-2xl">Change Password</h1>
        <p className="font-poppins text-white/40 text-sm mt-1">
          Keep your account secure by using a strong password.
        </p>
      </div>

      {/* Card */}
      <div
        className="rounded-2xl flex flex-col gap-6 p-7"
        style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <PasswordField
          label="Current Password"
          value={current}
          onChange={val => { setCurrent(val); setCurrentError(''); setStatus(null); }}
          error={currentError}
        />

        <div style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.06)' }} />

        <PasswordField
          label="New Password"
          description="Use at least 8 characters."
          value={newPass}
          onChange={setNewPass}
        />

        <PasswordField
          label="Confirm New Password"
          value={confirm}
          onChange={setConfirm}
          error={mismatch ? 'Passwords do not match.' : ''}
        />
      </div>

      {/* Save row */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="font-poppins font-bold text-sm px-8 py-3 rounded-xl transition-all duration-200 focus:outline-none"
          style={{
            backgroundColor: canSave ? '#F7941D' : 'rgba(247,148,29,0.25)',
            color: canSave ? '#1A1A1A' : 'rgba(255,255,255,0.2)',
            cursor: canSave ? 'pointer' : 'not-allowed',
            minWidth: 160,
          }}
          onMouseEnter={e => { if (canSave) e.currentTarget.style.backgroundColor = '#ffb83f'; }}
          onMouseLeave={e => { if (canSave) e.currentTarget.style.backgroundColor = '#F7941D'; }}
        >
          {saving ? 'Updating…' : 'Update Password'}
        </button>

        {status === 'success' && (
          <div className="flex items-center gap-2">
            <span style={{ color: '#4ade80', fontSize: 16 }}>✓</span>
            <p className="font-poppins text-sm font-medium" style={{ color: '#4ade80' }}>
              Password updated successfully!
            </p>
          </div>
        )}
      </div>

    </div>
  );
}

export default ChangePasswordSection;
